import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';


const ClientSuccessSection = () => {
  const caseStudies = [
  {
    company: 'Stryker Corporation',
    logo: 'Building2',
    industry: 'Orthopedic & Surgical Devices',
    challenge: 'Managing policy changes across 15+ device categories with limited compliance team',
    solution: 'Implemented PolicyAlerts enterprise platform with custom category monitoring and automated alert routing',
    results: [
    { metric: '87%', label: 'Time Savings', icon: 'Clock' },
    { metric: '$2.4M', label: 'Annual Cost Reduction', icon: 'DollarSign' },
    { metric: '100%', label: 'Policy Coverage', icon: 'Target' }],

    testimonial: "PolicyAlerts transformed our compliance operations. We now catch critical policy changes weeks before they impact our business, giving us time to adapt strategically rather than react frantically.",
    author: 'Sarah Mitchell',
    role: 'VP of Regulatory Affairs',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b10e24c-1764649836727.png",
    imageAlt: 'Professional woman executive in business attire reviewing regulatory compliance documents at modern office desk with multiple monitors displaying policy tracking dashboards'
  },
  {
    company: 'Medtronic',
    logo: 'Heart',
    industry: 'Cardiovascular Devices',
    challenge: 'Tracking CMS coverage determinations across multiple cardiac device portfolios',
    solution: 'Deployed PolicyAlerts with specialized cardiovascular monitoring and reimbursement tracking',
    results: [
    { metric: '92%', label: 'Faster Detection', icon: 'Zap' },
    { metric: '45', label: 'Compliance Issues Prevented', icon: 'ShieldCheck' },
    { metric: '3,200', label: 'Hours Saved Annually', icon: 'TrendingUp' }],

    testimonial: "The platform's cardiovascular-specific intelligence has been invaluable. We're now proactive instead of reactive, and our executive team has complete confidence in our regulatory oversight.",
    author: 'Dr. James Chen',
    role: 'Chief Compliance Officer',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cdeac712-1769463258217.png",
    imageAlt: 'Asian male executive doctor in white coat and business suit analyzing medical device compliance data on tablet in modern healthcare facility boardroom'
  },
  {
    company: 'Boston Scientific',
    logo: 'Activity',
    industry: 'Multi-Category Medical Devices',
    challenge: 'Coordinating policy monitoring across global operations with regional compliance requirements',
    solution: 'Enterprise deployment with multi-region monitoring and custom integration with existing compliance systems',
    results: [
    { metric: '15', label: 'Regions Monitored', icon: 'Globe' },
    { metric: '99.8%', label: 'Alert Accuracy', icon: 'CheckCircle2' },
    { metric: '$5.2M', label: 'Risk Mitigation Value', icon: 'Shield' }],

    testimonial: "PolicyAlerts scales with our global operations seamlessly. The platform\'s reliability and comprehensive coverage give us peace of mind that we\'re never missing critical regulatory changes.",
    author: 'Michael Rodriguez',
    role: 'Global Head of Regulatory Intelligence',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16ec06dd9-1767456565993.png",
    imageAlt: 'Hispanic male business executive in navy suit presenting regulatory compliance strategy on large screen in corporate conference room with global operations map'
  }];


  return (
    <section className="bg-white py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#10B981]/10 border border-[#10B981]/20 rounded-full px-4 py-2 mb-4">
            <Icon name="Award" size={16} color="#10B981" />
            <span className="text-xs md:text-sm font-medium text-[#10B981]">Client Success Stories</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B1B20] mb-4">
            Proven Results with Industry Leaders
          </h2>
          <p className="text-base md:text-lg text-[#6B7280] max-w-3xl mx-auto">
            See how Fortune 500 medical device manufacturers achieve measurable compliance outcomes with PolicyAlerts
          </p>
        </div>

        <div className="space-y-8 md:space-y-12">
          {caseStudies?.map((study, index) =>
          <div key={index} className="bg-[#FAFBFC] rounded-2xl overflow-hidden shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="p-6 md:p-8 lg:p-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1A73E8]/10 rounded-xl flex items-center justify-center">
                      <Icon name={study?.logo} size={28} color="#1A73E8" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-[#1B1B20]">{study?.company}</h3>
                      <p className="text-sm text-[#6B7280]">{study?.industry}</p>
                    </div>
                  </div>

                  <div className="space-y-6 mb-6 md:mb-8">
                    <div>
                      <h4 className="text-sm font-semibold text-[#1B1B20] mb-2 flex items-center gap-2">
                        <Icon name="AlertCircle" size={16} color="#ED1C24" />
                        Challenge
                      </h4>
                      <p className="text-sm md:text-base text-[#6B7280]">{study?.challenge}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-[#1B1B20] mb-2 flex items-center gap-2">
                        <Icon name="Lightbulb" size={16} color="#1A73E8" />
                        Solution
                      </h4>
                      <p className="text-sm md:text-base text-[#6B7280]">{study?.solution}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6 md:mb-8">
                    {study?.results?.map((result, idx) =>
                  <div key={idx} className="bg-white rounded-xl p-4 text-center border border-[#E5E7EB]">
                        <div className="w-8 h-8 bg-[#10B981]/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                          <Icon name={result?.icon} size={16} color="#10B981" />
                        </div>
                        <div className="text-xl md:text-2xl font-bold text-[#1B1B20] mb-1">{result?.metric}</div>
                        <div className="text-xs text-[#6B7280]">{result?.label}</div>
                      </div>
                  )}
                  </div>

                  <div className="bg-white rounded-xl p-6 border-l-4 border-[#1A73E8]">
                    <p className="text-sm md:text-base text-[#1B1B20] italic mb-4 leading-relaxed">
                      "{study?.testimonial}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1A73E8]/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={20} color="#1A73E8" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#1B1B20]">{study?.author}</div>
                        <div className="text-xs text-[#6B7280]">{study?.role}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <Image
                  src={study?.image}
                  alt={study?.imageAlt}
                  className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B20]/60 to-transparent"></div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <div className="bg-gradient-to-r from-[#1A73E8] to-[#0066FF] rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Join Industry Leaders</h3>
            <p className="text-base md:text-lg opacity-90 mb-6 md:mb-8 max-w-2xl mx-auto">
              See how PolicyAlerts can transform your compliance operations with proven enterprise solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-white text-[#1B1B20] hover:bg-gray-100">

                Schedule Executive Briefing
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10">

                Download Case Studies
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ClientSuccessSection;